import React from 'react';
import {
  Card,
  Form,
  Button,
  Input,
  Select,
  Row,
  Col,
  Icon,
  Tooltip,
  Switch,
  message as showMessage,
} from 'antd';

import { patchData, getData } from '../../helper/dataService';
import API from '../../helper/api';
import Spinner from '../common/Spinner';
import { delayApiRequest, objDifferenceWith } from '../../helper/utility';

class AssetEdit extends React.Component {
  state = {
    fetching: true,
    data: {},
    deviceFetching: false,
    deviceData: [],
    organizationData: [],
    organizationFetching: false,
    servicePlanFetching: false,
    servicePlanData: [],
    submitted: false,
  };

  EDITABLE_FIELDS = [];
  ORIGINAL_DATA = {};

  componentDidMount() {
    this.fetchAssetData();
    this.fetchServicePlanList();
  }

  fetchAssetData = () => {
    getData(API.asset.editOrView(this.props.match.params.oid))
      .then(({ data: { data, status, editable } }) => {
        if (status === 'success') {
          this.EDITABLE_FIELDS = [...editable];
          const {
            active,
            allow_view,
            devices,
            name,
            remarks,
            organization,
            asset_plate_number,
            asset_type,
            service_plan,
            device_sim_operator,
            device_sim_number,
            sim_number, // Fallback for backward compatibility
          } = data;
          let device_oid = '';
          if (devices.length) {
            device_oid = devices[0]._id.$oid;
          }

          let organization_oid = '';
          if (organization && organization._id) {
            organization_oid = organization._id.$oid;
            this.setState({ organizationData: [organization] });
          }

          let service_plan_value = '';
          if (service_plan) {
            // Handle existing assets that return service_plan as full object, {$oid} wrapper, or plain string id
            if (service_plan._id && service_plan._id.$oid) {
              service_plan_value = service_plan._id.$oid;
            } else if (service_plan.$oid) {
              service_plan_value = service_plan.$oid;
            } else if (typeof service_plan === 'string') {
              service_plan_value = service_plan;
            }
          }

          const finalData = {
            active,
            allow_view,
            device_oid,
            name,
            remarks,
            organization_oid,
            asset_plate_number: asset_plate_number || '',
            asset_type: asset_type || '',
            service_plan: service_plan_value,
            device_sim_operator: device_sim_operator || '',
            device_sim_number: device_sim_number || sim_number || '',
          };
          this.props.form.setFieldsValue({ ...finalData });

          this.ORIGINAL_DATA = { ...finalData };

          this.setState({ data, deviceData: devices });
        } else if (status === 'failed') {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ fetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ fetching: false });
      });
  };

  //* Device
  handleFetchDevice = (deviceName) => {
    if (deviceName.length >= 4) {
      delayApiRequest(() => this.fetchDevice(deviceName));
    }
  };

  fetchDevice = (deviceName) => {
    this.props.form.resetFields(['device_oid']);

    this.setState({ deviceData: [], deviceFetching: true });
    getData(API.device.search(deviceName))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ deviceData: data });
        } else {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ deviceFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ deviceFetching: false });
      });
  };

  clearDevice = () => {
    this.setState({ deviceData: [] });
    this.props.form.setFieldsValue({ device_oid: '' });
  };

  //* Organization
  handleFetchOrganization = (organizationName) => {
    if (organizationName.length >= 3) {
      delayApiRequest(() => this.fetchOrganization(organizationName));
    }
  };

  fetchOrganization = (organizationName) => {
    this.props.form.resetFields(['organization_oid']);

    this.setState({ organizationData: [], organizationFetching: true });
    getData(API.organization.search(organizationName))
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ organizationData: data });
        } else {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ organizationFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ organizationFetching: false });
      });
  };

  clearOrganization = () => {
    this.setState({ organizationData: [] });
    this.props.form.resetFields(['organization_oid']);
  };

  //* Service Plan
  fetchServicePlanList = () => {
    this.setState({ servicePlanFetching: true });
    getData(API.servicePlan.list)
      .then(({ data: { data, status } }) => {
        if (status === 'success') {
          this.setState({ servicePlanData: data });
        } else {
          showMessage.error('Sorry! Something went wrong');
        }
        this.setState({ servicePlanFetching: false });
      })
      .catch(() => {
        showMessage.error('Sorry! Something went wrong');
        this.setState({ servicePlanFetching: false });
      });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const data = objDifferenceWith(this.ORIGINAL_DATA, values);

        const finalData = { ...data };
        if (finalData.hasOwnProperty('device_oid')) {
          finalData.devices = finalData.device_oid
            ? [finalData.device_oid]
            : [];
          delete finalData.device_oid;
        }

        if (Object.keys(finalData).length) {
          this.setState({ submitted: true });
          patchData(
            API.asset.editOrView(this.props.match.params.oid),
            finalData
          )
            .then(({ data: { status, display } }) => {
              this.ORIGINAL_DATA = { ...this.ORIGINAL_DATA, ...data };
              if (status === 'success') {
                showMessage.success(display);
              } else if (status === 'failed') {
                showMessage.error(display);
              }
              this.setState({ submitted: false });
            })
            .catch(() => {
              showMessage.error('Sorry! Something went wrong');
              this.setState({ submitted: false });
            });
        }
      }
    });
  };

  render() {
    const { getFieldDecorator, getFieldValue } = this.props.form;
    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
      },
    };
    const {
      fetching,
      deviceFetching,
      deviceData,
      organizationData,
      organizationFetching,
      servicePlanFetching,
      servicePlanData,
      submitted,
      data: { asset_id = '' },
    } = this.state;

    const currentSelectedDevice = deviceData.find(
      ({ _id }) => _id.$oid === getFieldValue('device_oid')
    );
    const currentSelectedOrganization = organizationData.find(
      ({ _id }) => _id.$oid === getFieldValue('organization_oid')
    );

    return (
      <Row type='flex' justify='space-around' align='middle'>
        <Col xs={24} sm={18}>
          <Card
            title={
              <span className='title'>
                <Icon type='car' />
                {' Edit Asset'}
              </span>
            }
            extra={<span>{asset_id}</span>}
            loading={fetching}
          >
            <Form onSubmit={this.handleSubmit} layout='vertical'>
              <Form.Item
                label='Organization'
                extra={
                  currentSelectedOrganization ? (
                    <Button.Group size='small'>
                      <Button>{currentSelectedOrganization.name}</Button>
                      <Tooltip title='Remove Organization'>
                        <Button
                          icon='delete'
                          onClick={this.clearOrganization}
                          disabled={
                            !this.EDITABLE_FIELDS.includes('organization')
                          }
                        />
                      </Tooltip>
                    </Button.Group>
                  ) : null
                }
                {...formItemLayout}
              >
                {getFieldDecorator('organization_oid', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select a organization',
                    },
                  ],
                })(
                  <Select
                    showSearch
                    placeholder='Search and Select Organization'
                    filterOption={false}
                    notFoundContent={
                      organizationFetching ? <Spinner /> : 'No Content Found'
                    }
                    onSearch={this.handleFetchOrganization}
                    defaultActiveFirstOption={false}
                    disabled={!this.EDITABLE_FIELDS.includes('organization')}
                  >
                    {organizationData.length > 0 &&
                      organizationData.map(({ _id, name }) => (
                        <Select.Option key={_id.$oid}>{name}</Select.Option>
                      ))}
                  </Select>
                )}
              </Form.Item>

              <Form.Item
                label='Device IMEI'
                extra={
                  currentSelectedDevice ? (
                    <Button.Group size='small'>
                      <Button>
                        {currentSelectedDevice.imei} [
                        {currentSelectedDevice.device_id}] [
                        {currentSelectedDevice.device_type}]
                      </Button>
                      <Tooltip title='Remove device'>
                        <Button
                          icon='delete'
                          onClick={this.clearDevice}
                          disabled={
                            !this.EDITABLE_FIELDS.includes('devices') ||
                            !this.ORIGINAL_DATA.device_oid
                          }
                        />
                      </Tooltip>
                    </Button.Group>
                  ) : null
                }
                {...formItemLayout}
              >
                {getFieldDecorator(
                  'device_oid',
                  {}
                )(
                  <Select
                    showSearch
                    placeholder='Search and Select Device IMEI'
                    filterOption={false}
                    notFoundContent={
                      deviceFetching ? <Spinner /> : 'No Content Found'
                    }
                    onSearch={this.handleFetchDevice}
                    defaultActiveFirstOption={false}
                    disabled={
                      !this.EDITABLE_FIELDS.includes('devices') ||
                      !this.ORIGINAL_DATA.device_oid
                    }
                  >
                    {deviceData.length > 0 &&
                      deviceData.map(
                        ({ _id, imei, device_id, device_type }) => (
                          <Select.Option key={_id.$oid}>
                            {imei} [{device_id}] [{device_type}]
                          </Select.Option>
                        )
                      )}
                  </Select>
                )}
              </Form.Item>
              <Form.Item label='Asset Name' {...formItemLayout}>
                {getFieldDecorator('name', {
                  rules: [
                    {
                      required: true,
                      message: 'Please enter asset name',
                    },
                  ],
                })(
                  <Input
                    placeholder='Enter asset name'
                    disabled={!this.EDITABLE_FIELDS.includes('name')}
                  />
                )}
              </Form.Item>
              <Form.Item label='Asset Plate Number' {...formItemLayout}>
                {getFieldDecorator('asset_plate_number', {})(
                  <Input
                    placeholder='Enter asset plate number'
                    disabled={
                      !this.EDITABLE_FIELDS.includes('asset_plate_number')
                    }
                  />
                )}
              </Form.Item>
              <Form.Item label='Asset Type' {...formItemLayout}>
                {getFieldDecorator('asset_type', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select asset type',
                    },
                  ],
                })(
                  <Select
                    placeholder='Select Asset Type'
                    disabled={!this.EDITABLE_FIELDS.includes('asset_type')}
                  >
                    <Select.Option value='Car'>Car</Select.Option>
                    <Select.Option value='Bus'>Bus</Select.Option>
                    <Select.Option value='Jeep'>Jeep</Select.Option>
                    <Select.Option value='Motorcycle'>Motorcycle</Select.Option>
                    <Select.Option value='Truck'>Truck</Select.Option>
                    <Select.Option value='Microbus'>Microbus</Select.Option>
                  </Select>
                )}
              </Form.Item>
              <Form.Item label='Service Plan' {...formItemLayout}>
                {getFieldDecorator('service_plan', {
                  rules: [
                    {
                      required: true,
                      message: 'Please select a service plan',
                    },
                  ],
                })(
                  <Select
                    placeholder='Select Service Plan'
                    loading={servicePlanFetching}
                    notFoundContent={
                      servicePlanFetching ? <Spinner /> : 'No Content Found'
                    }
                    disabled={!this.EDITABLE_FIELDS.includes('service_plan')}
                  >
                    {servicePlanData.length > 0 &&
                      servicePlanData.map(({ _id, plan_name }) => (
                        <Select.Option key={_id.$oid} value={_id.$oid}>
                          {plan_name}
                        </Select.Option>
                      ))}
                  </Select>
                )}
              </Form.Item>
              <Form.Item label='Sim Operator' {...formItemLayout}>
                {getFieldDecorator('device_sim_operator', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input sim operator',
                    },
                    {
                      max: 100,
                      message: 'The input not more than 100 characters.',
                    },
                  ],
                })(
                  <Input
                    placeholder='Sim Operator'
                    disabled={
                      !(
                        this.EDITABLE_FIELDS.includes('device_sim_operator') 
                       
                      )
                    }
                  />
                )}
              </Form.Item>
              <Form.Item label='Sim Number' {...formItemLayout}>
                {getFieldDecorator('device_sim_number', {
                  rules: [
                    {
                      required: true,
                      message: 'Please input sim number',
                    },
                    {
                      max: 50,
                      message: 'The input not more than 50 characters.',
                    },
                  ],
                })(
                  <Input
                    placeholder='Sim Number'
                    disabled={!this.EDITABLE_FIELDS.includes('device_sim_number')}
                  />
                )}
              </Form.Item>
              <Form.Item label='Remarks' {...formItemLayout}>
                {getFieldDecorator('remarks', {
                  initialValue: '',
                  rules: [
                    {
                      max: 250,
                      message: 'The input not more than 250 characters.',
                    },
                  ],
                })(
                  <Input.TextArea
                    placeholder='Remarks'
                    rows={4}
                    disabled={!this.EDITABLE_FIELDS.includes('remarks')}
                  />
                )}
              </Form.Item>
              <Form.Item label='Active' {...formItemLayout}>
                {getFieldDecorator('active', {
                  valuePropName: 'checked',
                  initialValue: true,
                })(
                  <Switch disabled={!this.EDITABLE_FIELDS.includes('active')} />
                )}
              </Form.Item>
              <Form.Item label='Allow View' {...formItemLayout}>
                {getFieldDecorator('allow_view', {
                  valuePropName: 'checked',
                  initialValue: true,
                })(
                  <Switch
                    disabled={!this.EDITABLE_FIELDS.includes('allow_view')}
                  />
                )}
              </Form.Item>

              <Button
                type='primary'
                size='large'
                htmlType='submit'
                style={{ float: 'right' }}
                loading={submitted}
              >
                Submit
              </Button>
            </Form>
          </Card>
        </Col>
      </Row>
    );
  }
}

export default Form.create()(AssetEdit);
